/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  ButtonProps,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { GridAddIcon, GridDeleteIcon } from "@mui/x-data-grid";
import { useQueryClient } from "@tanstack/react-query";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { ReactComponent as Export } from "../../assets/icons/export.svg";
import { COLORS } from "../../assets/theme";
import TruncatedText from "../../components/common/TruncatedText";
import Actions from "../../components/common/Actions";
import CustomDialog from "../../components/common/CustomDialog";
import CustomLabel from "../../components/common/CustomLabel";
import { ICustomSelectProps } from "../../components/common/CustomSelect";
import SearchForm from "../../components/common/SearchForm";
import CustomTable from "../../components/common/table/CustomTable";
import SearchHeaderContainer from "../../components/common/table/SearchHeaderContainer";
import FileUploadField from "../../components/FileUploadField";
import ToastNotification from "../../components/ToastNotification";
import { getCountryNameOptions } from "../../services/CountryService";
import {
  addOperatorPrefixApi,
  deleteOperatorPrefixApi,
  editOperatorPrefixApi,
  exportOperatorPrefix,
  fetchOperatorPrefixHistory,
  getOperatorPrefixById,
  OperatorPrefixHistory,
  OperatorPrefixHistoryDiff,
  OperatorPrefixType,
  uploadOperatorPrefixesExcel,
  useOperatorPrefixesQuery,
} from "../../services/OperatorPrefixesService";
import {
  getOperatorMccOptions,
  getOperatorMncOptions,
  getOperatorNameOptions,
} from "../../services/OperatorsService";
import { IField, Option, Params } from "../../types/table/interfaces.type";
import {
  loadCountriesOptions,
  loadOperatorsByCountry,
} from "../../utils/commonDataUtils";
import CustomTextField from "../../components/common/CustomTextField";
import ExportTable from "../../components/ExportTable";

import History from "../../components/History";
import AutoComplete, { IAutoCompleteProps } from "../../components/common/AutoComplete";
import GetErrors from "../../components/GetErrors";
import { IExport } from "../../services/CostPlanService";
import { hasJwtPermission } from "../../services/ServicesHelpers";

const OperatorPrefixesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [isOperatorDisabled, setIsOperatorDisabled] = useState(true);
  const [filters, setFilters] = useState<
    Params<OperatorPrefixType & { countryId: number }>
  >({});
  const [appliedFilters, setAppliedFilters] = useState<
    Params<OperatorPrefixType>
  >({
    search: "",
    prefixNumber: "",
  });
  const [countriesOptions, setCountriesOptions] = useState<Option[]>([]);
  const [operatorsByCountry, setOperatorsByCountry] = useState<Option[]>([]);

  const dialogRef = useRef<OperatorPrefixDialogRef>(null);

  const handleOpenDialog = (
    mode: "add" | "edit" | "delete" | "history",
    id?: string
  ) => {
    dialogRef.current?.openDialog(mode, id);
  };

  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);

  useEffect(() => {
    setSelectedRowIds([]);
  }, [filters]);

  const onRowSelect = (row: OperatorPrefixType, checked: boolean) => {
    console.log(`Row ID ${row.id} selection changed: ${checked}`);
    setSelectedRowIds((prevSelected) => {
      const newSelected = checked
        ? [...prevSelected, String(row.id)]
        : prevSelected.filter((id) => id !== String(row.id));
      console.log("Selected row IDs:", newSelected);
      return newSelected;
    });
  };

  const isRowSelected = (row: OperatorPrefixType) =>
    selectedRowIds.includes(String(row.id));

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const countries = await loadCountriesOptions();
        setCountriesOptions(countries);
      } catch (error: any) {
        ToastNotification.error("Error loading countries.");
      }
    };
    loadInitialData();
  }, []);

  const handleOpenImportDialog = () => {
    dialogRef.current?.openImportDialog();
  };

  const handleSearchChange = (search: string) => {
    setAppliedFilters((prev) => ({ ...prev, search }));
  };

  const handleSearchClick = () => {
    setAppliedFilters((prev) => ({
      ...prev,
      ...(filters.operatorId
        ? { operatorId: filters.operatorId }
        : { operatorId: undefined }),
      ...(filters.countryId
        ? { countryId: filters.countryId }
        : { countryId: undefined }),
      prefixNumber: filters.prefixNumber,
    }));
    setSelectedRowIds([]);
  };

  const textFields = [
    {
      label: "Prefix Number",
      value: filters.prefixNumber,
      placeholder: "Prefix Number",
      helperText: "",
      error: false,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        setFilters({ ...filters, prefixNumber: e.target.value }),
      borderRadius: "8px",
    },
  ];

  const buttons = [
    {
      label: "Search",
      variant: "contained" as ButtonProps["variant"],
      bgColor: COLORS.GOLD,
      onClick: handleSearchClick,
      permission: "GET_ALL_OPERATOR_PREFIXES",
    },
  ];

  const autoCompletes: IAutoCompleteProps[] = [
    {
      label: "Country",
      value: filters.countryId ?? "",
      options: countriesOptions,
      placeholder: "Select Country",

      clearable: true,
      onChange: async (value: string | number | (string | number)[]) => {
        const selectedCountry = countriesOptions.find(
          (option) => option.value === value
        );

        setFilters((prev) => ({
          ...prev,
          countryId: Number(selectedCountry?.value),
          operatorId: undefined,
        }));

        if (selectedCountry) {
          try {
            setIsOperatorDisabled(true);
            const operators = await loadOperatorsByCountry(
              Number(selectedCountry.value)
            );
            setOperatorsByCountry(operators);
          } catch (error: any) {
            ToastNotification.error(error.message);

            setOperatorsByCountry([]);
          } finally {
            setIsOperatorDisabled(false);
          }
        }
      },
    },
    {
      label: "Operator",
      value: filters.operatorId ?? "",
      options: operatorsByCountry,
      disabled: isOperatorDisabled,

      clearable: true,
      placeholder: "Select Operator",
      onChange: (value: string | number | (string | number)[]) => {
        const selectedOperator = operatorsByCountry.find(
          (option) => option.value === value
        );

        setFilters((prev) => ({
          ...prev,
          operatorId: Number(selectedOperator?.value),
        }));
      },
    },
  ];

  const fields: IField<OperatorPrefixType>[] = [
    {
      key: "countryId",
      header: "Country Name",
      options: (t) => getCountryNameOptions(t.countryId),
    },
    {
      key: "operatorId",
      header: "Operator Name",
      options: (t) => getOperatorNameOptions(t.operatorId),
    },
    {
      key: "operatorId",
      header: "MCC",
      options: (t) => getOperatorMccOptions(t.operatorId),
    },
    {
      key: "operatorId",
      header: "MNC",
      transform: async (t) => {
          if (!t.operatorId) return "-";
          try {
            const operator = await queryClient.fetchQuery(
              getOperatorMncOptions(t.operatorId),
            );
            return <TruncatedText text={operator?.mnc?.join(",")} />;
          } catch {
            return "-";
          }
        },
    },
    { key: "prefixNumber", header: "Prefix Number" },
    { key: "createdDate", header: "Created Date" },
  ];
  const actions = Actions({
    handleOpenEditModal: {
      handler: async (id: string | number) => {
        handleOpenDialog("edit", String(id));
      },
      isDisabled: !hasJwtPermission("GET_OPERATOR_PREFIX"),
    },
    onViewHistory: {
      // isDisabled: true,
      handler: async (id: string | number) => {
        handleOpenDialog("history", String(id));
      },
      isDisabled: !hasJwtPermission("GET_ALL_OPERATOR_PREFIXES_HISTORY"),
    },
  });

  return (
    <Box padding={isSmallScreen ? 2 : 3}>
      <CustomLabel
        text="Prefixes"
        variant={isSmallScreen ? "h6" : "h5"}
        fontWeight={500}
        mb={2}
      />

      <Box sx={{ mb: 3 }}>
        <SearchForm
          textFields={textFields}
          autoCompletes={autoCompletes}
          buttons={buttons}
        />
      </Box>

      <CustomLabel
        text="Prefix List"
        variant={isSmallScreen ? "h6" : "h5"}
        fontWeight={500}
        mb={1}
      />

      <SearchHeaderContainer
        search={appliedFilters.search ?? ""}
        setSearch={handleSearchChange}
        buttonConfig={[
          {
            size: "small",
            onClick: handleOpenImportDialog,
            textColor: COLORS.GOLD,
            label: "Import Prefix",
            variant: "outlined",
            borderColor: COLORS.GOLD,
            permission: "IMPORT_OPERATOR_PREFIX",
          },
          {
            size: "small",
            bgColor: COLORS.GOLD,
            startIcon: <GridAddIcon />,
            onClick: () => handleOpenDialog("add"),
            textColor: COLORS.iconColor,
            permission: "POST_OPERATOR_PREFIX",
          },
          {
            size: "small",
            bgColor: COLORS.GOLD,
            variant: "outlined" as ButtonProps["variant"],
            startIcon: <GridDeleteIcon />,
            onClick: () => handleOpenDialog("delete"),
            textColor: COLORS.iconColor,
            permission: "DELETE_OPERATOR_PREFIX",
          },
          {
            size: "small",
            bgColor: COLORS.GOLD,
            startIcon: <Export />,
            onClick: async () => {
              try {
                const filteredParams = Object.entries(appliedFilters).reduce<
                  Params<OperatorPrefixType>
                >((acc, [key, value]) => {
                  if (value !== undefined && value !== null && value !== "") {
                    acc[key as keyof typeof appliedFilters] = value as any;
                  }
                  return acc;
                }, {});

                await exportOperatorPrefix(filteredParams);
                ToastNotification.success("Export completed successfully!");
              } catch (error: any) {
                ToastNotification.error(
                  error.message || "Export failed. Please try again."
                );
              }
            },
            showExportProgress: true,
            textColor: COLORS.iconColor,
            label: "Export",
            permission: "GET_ALL_OPERATOR_PREFIXES_CSV",
          },
        ]}
      />

      <CustomTable<OperatorPrefixType>
        fields={fields}
        filters={appliedFilters}
        fetchFunc={useOperatorPrefixesQuery}
        actions={actions}
        checkboxSelect={true}
        onRowSelect={onRowSelect}
        isRowSelected={isRowSelected}
        sortableFields={["prefixNumber", "createdDate"]}
      />

      <OperatorPrefixDialog
        ref={dialogRef}
        onRefresh={() => {
          setAppliedFilters((prev) => ({ ...prev }));
          queryClient.invalidateQueries();
        }}
        selectedRowIds={selectedRowIds}
        onClearSelection={() => setSelectedRowIds([])}
        countriesOptions={countriesOptions}
        loadOperatorsByCountry={loadOperatorsByCountry}
        appliedFilters={appliedFilters}
      />
    </Box>
  );
};

export interface OperatorPrefixDialogRef {
  openDialog: (
    mode: "add" | "edit" | "delete" | "history",
    id?: string | number
  ) => void;
  openImportDialog: () => void;
  closeDialog: () => void;
  isDialogOpen: () => boolean;
  getDialogState: () => {
    mode: string | null;
    id: string | number | undefined;
  };
}

interface OperatorDialogProps {
  onRefresh: () => void;
  countriesOptions: Option[];
  loadOperatorsByCountry: (countryId: number) => Promise<Option[]>;
  selectedRowIds: string[];
  onClearSelection: () => void;
  appliedFilters: Params<OperatorPrefixType>;
}

const OperatorPrefixDialog = forwardRef<
  OperatorPrefixDialogRef,
  OperatorDialogProps
>(
  (
    {
      onRefresh,
      countriesOptions,
      loadOperatorsByCountry,
      selectedRowIds,
      appliedFilters,
      onClearSelection,
    },
    ref
  ) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState<
      "add" | "edit" | "delete" | "history"
    >("add");
    const [selectedId, setSelectedId] = useState<string | number | undefined>();
    const [dialogData, setDialogData] = useState<
      Partial<Params<OperatorPrefixType>>
    >({});
    const [operatorsByCountry, setOperatorsByCountry] = useState<Option[]>([]);
    const [isOperatorDisabled, setIsOperatorDisabled] = useState(true);
    const [isSaveDisabled, setIsSaveDisabled] = useState(true);

    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const [importFile, setImportFile] = useState<File | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [textFields, setTextFields] = useState({
      prefixNumber: "Prefix Number",
      mnc: "MNC",
      mcc: "MCC",
      operatorName: "OperatorName",
      countryName: "CountryName",
    });
    const queryClient = useQueryClient();
    const [selectedExport, setSelectedExport] = useState<IExport | null>(null);
    const [errorDetailsDialogOpen, setErrorDetailsDialogOpen] = useState(false);

    const handleViewErrorDetails = (exportData: IExport) => {
      setSelectedExport(exportData);
      setErrorDetailsDialogOpen(true);
      setImportDialogOpen(false);
    };

    const handleCloseErrorDetails = () => {
      setErrorDetailsDialogOpen(false);
      setSelectedExport(null);
    };

    useImperativeHandle(ref, () => ({
      openDialog: (mode, id) => {
        setDialogMode(mode);
        setSelectedId(id);
        setDialogOpen(true);
        if (mode === "edit" && id) {
          loadEditData(String(id));
        }
      },
      openImportDialog: () => {
        setImportDialogOpen(true);
      },
      closeDialog: () => {
        setDialogOpen(false);
      },
      isDialogOpen: () => dialogOpen,
      getDialogState: () => ({ mode: dialogMode, id: selectedId }),
    }));

    const resetDialogData = () => {
      setDialogData({});
      setDialogOpen(false);
      setOperatorsByCountry([]);
      setIsSaveDisabled(true);

      setIsOperatorDisabled(true);
      setImportDialogOpen(false);
    };

    const loadEditData = async (id: string) => {
      try {
        const data = await getOperatorPrefixById(id);
        setDialogData(data);

        if (data.countryId) {
          setIsOperatorDisabled(false);
          const operators = await loadOperatorsByCountry(data.countryId);
          setOperatorsByCountry(operators);

          setDialogData((prev) => ({
            ...prev,
            operatorId: data.operatorId ? Number(data.operatorId) : undefined,
          }));
        }
      } catch {
        ToastNotification.error("Error loading edit data.");
      }
    };

    const handleCountryChange = async (
      value: string | number | (string | number)[]
    ) => {
      const selectedCountry = Array.isArray(value) ? value[0] : value;
      setDialogData((prev) => ({
        ...prev,
        countryId: Number(selectedCountry),
        operatorId: undefined,
      }));

      if (selectedCountry) {
        try {
          const countryId = Number(selectedCountry);
          if (isNaN(countryId)) {
            throw new Error("Invalid country ID");
          }

          setIsOperatorDisabled(false);
          const operators = await loadOperatorsByCountry(countryId);
          setOperatorsByCountry(operators);
        } catch (error: any) {
          ToastNotification.error(
            "Error fetching operators for selected country."
          );
          setOperatorsByCountry([]);
        }
      } else {
        setOperatorsByCountry([]);
        setIsOperatorDisabled(true);
      }
    };

    const validateForm = () => {
      const errors = {
        prefixNumber: !dialogData.prefixNumber,
        countryId: !dialogData.countryId,
      };
      setIsSaveDisabled(Object.values(errors).some(Boolean));
    };

    useEffect(validateForm, [dialogData]);

    const handleSave = async () => {
      if (isSaveDisabled) {
        ToastNotification.error("Please fill all required fields.");
        return;
      }
      try {
        if (dialogMode === "add") {
          await addOperatorPrefixApi({ ...dialogData, isDeleted: false });
          ToastNotification.success("Operator prefix added successfully!");
        } else if (dialogMode === "edit" && selectedId) {
          await editOperatorPrefixApi(String(selectedId), dialogData);
          ToastNotification.success("Operator prefix updated successfully!");
        }
        onRefresh();
        resetDialogData();
      } catch {
        ToastNotification.error("Error saving operator prefix.");
      }
    };

    const handleDelete = async () => {
      try {
        await deleteOperatorPrefixApi(selectedRowIds);
        ToastNotification.success("Operator prefix deleted successfully!");
        onClearSelection();
        onRefresh();
        setDialogOpen(false);
      } catch {
        ToastNotification.error("Error deleting operator prefix.");
      }
    };

    const handleUpload = async () => {
      if (!importFile) {
        ToastNotification.error("Please select a file.");
        return;
      }
      setIsUploading(true);
      try {
        await uploadOperatorPrefixesExcel(importFile, textFields);
        queryClient.invalidateQueries({ queryKey: ["importedFiles"] });
        queryClient.refetchQueries({ queryKey: ["importedFiles"] });
        ToastNotification.success("File uploaded successfully!");
        resetDialogData();
        setImportDialogOpen(true);
        setIsUploading(false);
        onRefresh();
      } catch (error: any) {
        ToastNotification.error(
          error?.response?.data?.message ||
            error?.message ||
            "Error uploading file."
        );
      } finally {
        setIsUploading(false);
      }
    };

    const fieldHistory: IField<OperatorPrefixType>[] = [
      {
        key: "countryId",
        header: "Country Name",
        options: (t) => getCountryNameOptions(t.countryId),
      },
      {
        key: "operatorId",
        header: "Operator Name",
        options: (t) => getOperatorNameOptions(t.operatorId),
      },
      {
        key: "operatorId",
        header: "MCC",
        options: (t) => getOperatorMccOptions(t.operatorId),
      },
      {
        key: "operatorId",
        header: "MNC",
        transform: async (t) => {
          if (!t.operatorId) return "-";
          try {
            const operator = await queryClient.fetchQuery(
              getOperatorMncOptions(t.operatorId),
            );
            return <TruncatedText text={operator?.mnc?.join(",")} />;
          } catch {
            return "-";
          }
        },
      },
      {
        key: "prefixNumber",
        header: "Prefix Number",
      },
      {
        key: "historyTimestamp",
        header: "Date Modifiedq",
      },
      { key: "createdBy", header: "CreadtedBy By" },
      { key: "updatedBy", header: "Updated By" },
    ];
    return (
      <>
        <CustomDialog
          open={dialogOpen}
          onClose={resetDialogData}
          mode={dialogMode}
          title={
            dialogMode === "history"
              ? "History"
              : dialogMode === "add"
              ? "Add New Prefix"
              : dialogMode === "edit"
              ? "Edit Prefix"
              : "Delete Prefix"
          }
          fields={
            dialogMode === "history"
              ? [
                  {
                    type: "custom",
                    component: (
                      <History
                        filters={appliedFilters}
                        fields={fieldHistory}
                        fetchFunc={({ size = 10 }) =>
                          fetchOperatorPrefixHistory(String(selectedId), {
                            historyTimestamp: appliedFilters.historyTimestamp
                              ? String(appliedFilters.historyTimestamp)
                              : undefined,
                            size,
                          })
                        }
                      />
                    ),
                  },
                ]
              : dialogMode === "delete"
              ? [
                  {
                    type: "custom",
                    component: (
                      <Box>Are you sure you want to delete this prefix?</Box>
                    ),
                  },
                ]
              : [
                  {
                    type: "custom",
                    component: (
                      <AutoComplete
                        label="Country"
                        value={dialogData.countryId || ""}
                        options={countriesOptions}
                        onChange={(value) => handleCountryChange(value)}
                        clearable={true}
                        disablePortal={false}
                        placeholder="Select Country"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: COLORS.WhiteBackground,
                          },
                        }}
                      />
                    ),
                  },
                  {
                    type: "custom",
                    component: (
                      <AutoComplete
                        label="Operator"
                        value={dialogData.operatorId || ""}
                        options={operatorsByCountry}
                        onChange={(value: any) =>
                          setDialogData((prev) => ({
                            ...prev,
                            operatorId: Number(value),
                          }))
                        }
                        clearable={true}
                        disablePortal={false}
                        placeholder="Select Operator"
                        disabled={isOperatorDisabled}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: COLORS.WhiteBackground,
                          },
                        }}
                      />
                    ),
                  },
                  {
                    type: "text",
                    label: "Prefix Number",
                    bgColor: COLORS.WhiteBackground,
                    value: dialogData.prefixNumber || "",
                    onChange: (value) =>
                      setDialogData((prev) => ({
                        ...prev,
                        prefixNumber: String(value),
                      })),
                    placeholder: "Enter prefix number",
                  },
                ]
          }
          buttons={
            dialogMode === "history"
              ? []
              : [
                  dialogMode === "delete"
                    ? {
                        label: "Delete",
                        onClick: handleDelete,
                        bgColor: COLORS.GOLD,
                        permission: "DELETE_OPERATOR_PREFIX",
                      }
                    : {
                        label: "Save",
                        onClick: handleSave,
                        disabled:
                          isSaveDisabled ||
                          (dialogMode === "edit"
                            ? !hasJwtPermission("PUT_OPERATOR_PREFIX")
                            : !hasJwtPermission("POST_OPERATOR_PREFIX")),
                        bgColor: COLORS.GOLD,
                      },
                ]
          }
        />
        <CustomDialog
          open={importDialogOpen}
          onClose={resetDialogData}
          title="Import Prefixes"
          dialogSx={{
            minWidth: { xs: "100vw", sm: "560px", md: "760px", lg: "980px", xl: "1100px" },
            maxWidth: { xs: "100vw", sm: "90vw", md: "85vw", lg: "75vw", xl: "1100px" },
            width: "100%",
            maxHeight: "88vh",
            overflow: "hidden",
          }}
          contentSx={{
            maxHeight: {
              xs: "55vh",
              sm: "60vh",
            },
            overflowY: "auto",
            paddingBottom: "8px",
          }}
          type="import"
          fields={[
            {
              type: "custom",

              component: (
                <Box>
                  <FileUploadField
                    accept=".xlsx,.xls,.csv"
                    title="File"
                    placeholder="Add Excel file"
                    onFileChange={setImportFile}
                  />
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                      Excel Header Fields
                    </Typography>
                  </Box>

                  <Grid
                    container
                    spacing={2}
                    sx={{ overflowX: "none" }}
                    key="text-fields-row"
                  >
                    <Grid component={"div" as React.ElementType}>
                      <CustomTextField
                        label="Prefix Number Header"
                        placeholder="Prefix Number Header"
                        size="small"
                        highlightLabelStar
                        bgColor={COLORS.WhiteBackground}
                        value={textFields.prefixNumber}
                        onChange={(e) =>
                          setTextFields({
                            ...textFields,
                            prefixNumber: e.target.value,
                          })
                        }
                      />
                    </Grid>
                    <Grid component={"div" as React.ElementType}>
                      <CustomTextField
                        label="MCC Header"
                        placeholder="MCC Header"
                        size="small"
                        highlightLabelStar
                        bgColor={COLORS.WhiteBackground}
                        value={textFields.mcc}
                        onChange={(e) =>
                          setTextFields({
                            ...textFields,
                            mcc: e.target.value,
                          })
                        }
                      />
                    </Grid>
                    <Grid component={"div" as React.ElementType}>
                      <CustomTextField
                        label="MNC Header"
                        placeholder="MNC Header"
                        size="small"
                        highlightLabelStar
                        bgColor={COLORS.WhiteBackground}
                        value={textFields.mnc}
                        onChange={(e) =>
                          setTextFields({
                            ...textFields,
                            mnc: e.target.value,
                          })
                        }
                      />
                    </Grid>
                    <Grid
                      component={"div" as React.ElementType}
                      xs={12}
                      sm={6}
                      md={2}
                    >
                      <CustomTextField
                        label="Operator Name Header"
                        placeholder="OperatorName"
                        size="small"
                        fullWidth
                        bgColor={COLORS.WhiteBackground}
                        value={textFields.operatorName}
                        onChange={(e) =>
                          setTextFields({
                            ...textFields,
                            operatorName: e.target.value,
                          })
                        }
                      />
                    </Grid>
                    <Grid
                      component={"div" as React.ElementType}
                      xs={12}
                      sm={6}
                      md={2}
                    >
                      <CustomTextField
                        label="Country Name Header"
                        placeholder="CountryName"
                        size="small"
                        fullWidth
                        bgColor={COLORS.WhiteBackground}
                        value={textFields.countryName}
                        onChange={(e) =>
                          setTextFields({
                            ...textFields,
                            countryName: e.target.value,
                          })
                        }
                      />
                    </Grid>
                  </Grid>
                </Box>
              ),
            },
            {
              type: "custom",
              component: (
                <ExportTable
                  usedBy="PREFIX"
                  onView={handleViewErrorDetails}
                  onCloseImportDialog={() => setImportDialogOpen(false)}
                />
              ),
            },
          ]}
          buttons={[
            dialogMode === "history"
              ? {}
              : {
                  label: "Upload",
                  onClick: handleUpload,
                  permission: "IMPORT_OPERATOR_PREFIX",
                },
          ]}
          mode={"add"}
        />

        <CustomDialog
          open={errorDetailsDialogOpen}
          onClose={handleCloseErrorDetails}
          title="View Error Details"
          mode="history"
          fields={[
            {
              type: "custom",
              component: (
                <GetErrors
                  usedBy="PREFIX"
                  fields={[
                    { key: "rowNumber", header: "Row Number" },
                    { key: "errorMessage", header: "Error Message" },
                    { key: "createdBy", header: "Created By" },
                    { key: "createdDate", header: "Created Date" },
                  ]}
                  exportId={String(selectedExport)}
                />
              ),
            },
          ]}
          buttons={[{ label: "Close", onClick: handleCloseErrorDetails }]}
        />
      </>
    );
  }
);

export default OperatorPrefixesPage;
